import { DOCUMENT, Inject, Injectable } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { CanonicalService } from "./canonical.service";
import { absoluteUrl, DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE, SITE_NAME } from "./seo.constants";

export interface PageSeo {
    title: string;
    description?: string;
    /** Router path for canonical / og:url; defaults to the current URL. */
    path?: string;
    /** Site path or absolute URL; defaults to the site social card. */
    image?: string | null;
    imageAlt?: string;
    type?: 'website' | 'article';
    noindex?: boolean;
    /** schema.org objects; nulls are skipped. */
    jsonLd?: (object | null | undefined)[];
    /** ISO date, for articles. */
    publishedTime?: string | null;
}

const INDEX = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const NOINDEX = 'noindex, nofollow';
const JSON_LD_ATTR = 'data-seo-jsonld';

/**
 * One call per page sets everything a crawler or link preview reads: title,
 * description, robots, canonical, Open Graph, Twitter and JSON-LD. Each call
 * replaces the previous page's values, so nothing leaks across navigations.
 * Works the same on the server (the tags land in the SSR HTML) and in the browser.
 */
@Injectable({
    providedIn: "root"
})
export class SeoService {
    constructor(
        private title: Title,
        private meta: Meta,
        private canonical: CanonicalService,
        @Inject(DOCUMENT) private dom: Document,
    ) { }

    setPage(cfg: PageSeo) {
        const description = (cfg.description || DEFAULT_DESCRIPTION).trim();
        const url = this.canonical.createCanonicalURL(cfg.path);
        const image = absoluteUrl(cfg.image || DEFAULT_OG_IMAGE);
        const type = cfg.type || 'website';

        this.title.setTitle(cfg.title);
        this.meta.removeTag("property='title'");
        this.meta.removeTag("name='keywords'");
        this.setName('description', description);
        this.setRobots(!!cfg.noindex);

        this.setProperty('og:locale', 'en_IN');
        this.setProperty('og:site_name', SITE_NAME);
        this.setProperty('og:type', type);
        this.setProperty('og:title', cfg.title);
        this.setProperty('og:description', description);
        this.setProperty('og:url', url);
        this.setProperty('og:image', image);
        this.setProperty('og:image:alt', cfg.imageAlt || cfg.title);

        this.setName('twitter:card', 'summary_large_image');
        this.setName('twitter:title', cfg.title);
        this.setName('twitter:description', description);
        this.setName('twitter:image', image);

        if (type === 'article' && cfg.publishedTime) {
            this.setProperty('article:published_time', cfg.publishedTime);
        } else {
            this.meta.removeTag("property='article:published_time'");
        }

        this.setJsonLd(cfg.jsonLd || []);
    }

    /** Robots only; used for routes that should never be indexed (404, signed-in pages). */
    setRobots(noindex: boolean) {
        this.setName('robots', noindex ? NOINDEX : INDEX);
    }

    setJsonLd(items: (object | null | undefined)[]) {
        const head = this.dom.head;
        head.querySelectorAll(`script[${JSON_LD_ATTR}]`).forEach(s => s.remove());
        for (const item of items) {
            if (!item) { continue; }
            const script = this.dom.createElement('script');
            script.setAttribute('type', 'application/ld+json');
            script.setAttribute(JSON_LD_ATTR, '');
            // "<" escaped so CMS text can never close the script element.
            script.textContent = JSON.stringify(item).replace(/</g, '\\u003c');
            head.appendChild(script);
        }
    }

    /** @deprecated use setPage */
    updateTitle(title: string) {
        this.title.setTitle(title);
    }

    /** @deprecated use setPage */
    updateMetaTag(name: any, property: any, desc: string) {
        if (property === 'title') { return; }
        if (name === null) {
            this.meta.updateTag({ property: property, content: desc });
        } else {
            this.meta.updateTag({ name: name, content: desc });
        }
    }

    private setName(name: string, content: string) {
        this.meta.updateTag({ name, content }, `name='${name}'`);
    }

    private setProperty(property: string, content: string) {
        this.meta.updateTag({ property, content }, `property='${property}'`);
    }
}
